import React, { useEffect, useCallback } from 'react';
import { Modal, Spin } from 'antd';
import { useNavigate } from 'react-router-dom';
import { usePW9224Mode } from './hooks/usePW9224Mode';
import { usePW9224Record } from './hooks/usePW9224Record';
import { usePW9224Navigation } from './hooks/usePW9224Navigation';
import { useLookupNames } from './hooks/useLookupNames';
import { F924 } from '../../services/pw9224Api';
import TripForm from './components/TripForm';
import ActionBar from './components/ActionBar';
import './PW9224Form.css';

const PW9224Form: React.FC = () => {
  const navigate = useNavigate();
  const { mode, setMode, isEditing } = usePW9224Mode();
  const { names, resolveAllNames, resolveSingleName, clearNames } = useLookupNames();
  const {
    record, formData, details, selectedRow, loading,
    setRecord, setFormData, setDetails, setSelectedRow,
    loadRecord, setField, getField, setDetailField, getDetailField,
    handleSave, handleDelete, addDetailRow, deleteDetailRow,
    revertToRecord, resetForAdd,
  } = usePW9224Record();

  const loadRecordWithNames = useCallback(async (id: string) => {
    await loadRecord(id);
  }, [loadRecord]);

  const { allIds, currentIdx, setCurrentIdx, loadList, navTo } = usePW9224Navigation(loadRecordWithNames);

  // Resolve names when formData changes
  useEffect(() => {
    if (formData.f924_11 || formData.f924_13 || formData.f924_91) {
      resolveAllNames(formData);
    }
  }, [formData.f924_11, formData.f924_13, formData.f924_91, formData.f924_72, formData.f924_81, formData.f924_84, resolveAllNames]);

  // Initial load
  useEffect(() => {
    const init = async () => {
      const ids = await loadList();
      if (ids.length > 0) {
        setCurrentIdx(0);
        await loadRecordWithNames(ids[0]);
      }
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle Add/Save action
  const handleAddAction = useCallback(async () => {
    if (!isEditing) {
      // Switch to add mode
      resetForAdd();
      clearNames();
      setMode('add');
    } else {
      // Save
      await handleSave(mode, async (result: F924) => {
        setRecord(result);
        setFormData(result);
        setDetails(result.details || []);
        setMode('browse');
        const ids = await loadList();
        const newIdx = ids.indexOf(result.f924_01);
        if (newIdx >= 0) setCurrentIdx(newIdx);
      });
    }
  }, [isEditing, mode, resetForAdd, clearNames, setMode, handleSave, setRecord, setFormData, setDetails, loadList, setCurrentIdx]);

  // Handle Modify/Revert action
  const handleModifyAction = useCallback(() => {
    if (!isEditing) {
      if (!record) return;
      // Check if record has approval status
      const status = (record.f924_80 || '').trim();
      if (status !== '') {
        Modal.warning({ title: '無法修改', content: '已核准/審核中的記錄不可修改' });
        return;
      }
      setMode('edit');
    } else {
      // Revert
      revertToRecord();
      setMode('browse');
    }
  }, [isEditing, record, setMode, revertToRecord]);

  // Handle Delete action
  const handleDeleteAction = useCallback(() => {
    if (!record) return;
    const status = (record.f924_80 || '').trim();
    if (status === 'Y' || status === 'N' || status === 'V' || status === 'P') {
      Modal.error({ title: '無法刪除', content: '已核,不可刪除!!' });
      return;
    }
    Modal.confirm({
      title: '刪除確認',
      content: '請確認刪除本資料!',
      okText: '確認',
      cancelText: '取消',
      onOk: async () => {
        const success = await handleDelete();
        if (success) {
          const ids = await loadList();
          if (ids.length > 0) {
            const newIdx = Math.min(currentIdx, ids.length - 1);
            setCurrentIdx(newIdx);
            await loadRecordWithNames(ids[newIdx]);
          } else {
            setRecord(null);
            setFormData({});
            setDetails([]);
          }
        }
      },
    });
  }, [record, currentIdx, handleDelete, loadList, setCurrentIdx, loadRecordWithNames, setRecord, setFormData, setDetails]);

  const handleNavTo = useCallback((idx: number) => {
    navTo(idx, isEditing);
  }, [navTo, isEditing]);

  const handleExit = useCallback(() => {
    navigate('/');
  }, [navigate]);

  return (
    <div className="pw9224-form">
      <div className="page-wrapper">
        <h2>出差單（含國內外）</h2>
        <Spin spinning={loading}>
          <TripForm
            formData={formData}
            details={details}
            names={names}
            mode={mode}
            isEditing={isEditing}
            selectedRow={selectedRow}
            setField={setField}
            setDetailField={setDetailField}
            setSelectedRow={setSelectedRow}
            resolveSingleName={resolveSingleName}
            addDetailRow={addDetailRow}
            deleteDetailRow={deleteDetailRow}
          />
        </Spin>
      </div>
      <ActionBar
        mode={mode}
        isEditing={isEditing}
        record={record}
        currentIdx={currentIdx}
        allIdsLength={allIds.length}
        onAdd={handleAddAction}
        onModify={handleModifyAction}
        onDelete={handleDeleteAction}
        onNavTo={handleNavTo}
        onExit={handleExit}
      />
    </div>
  );
};

export default PW9224Form;
